import {IHttpService} from "angular";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {ServiceWorkerService} from "../../../services/service-worker/ServiceWorkerService";
import {SubscriptionRequest} from "./subscription/SubscriptionRequest";
import "rxjs/add/operator/finally";
import {UserNotificationPreferences} from "./preferences/UserNotificationPreferences";
import {NotificationPreferencesRecord} from "./models/NotificationPreferencesRecord";

const BASE_URL = "/tradefloor/api/notifications";
const PREFERENCES_URL = `${BASE_URL}/preferences`;
const PUBLIC_KEY_URL = `${BASE_URL}/public-key`;
const SUBSCRIPTION_URL = `${BASE_URL}/register`;

// TODO Use the following to watch for permission changes: https://stackoverflow.com/a/39880338
export class UserNotificationPreferencesService {

    public static id = "userNotificationPreferencesService";

    private notificationsAvailable = new BehaviorSubject(false);
    private notificationsEnabled = new BehaviorSubject(false);
    private notificationsBlocked = new BehaviorSubject(false);
    private subscribed = false;

    private preferences$ = new ReplaySubject<UserNotificationPreferences>();

    /** @ngInject */
    public constructor(private $http: IHttpService, private serviceWorkerService: ServiceWorkerService) {
        this.init();
    }

    private init() {
        this.updatePermissionStatus();
        this.watchPermissionsChanges();

        // Save new state in the background
        this.preferences$
            .skip(1)
            .observeOn(Scheduler.async)
            .debounceTime(200)
            .subscribe((preferences) => this.savePreferenceState(preferences));

        this.initializeUserPreferences();
    }

    private updatePermissionStatus() {
        const available = "Notification" in window;
        if (available) {
            this.notificationsAvailable.next(true);
            // Workaround because the Typing info it incorrect in the current TS version
            const permission = (Notification as any).permission;
            const enabled = permission === "granted";
            this.notificationsEnabled.next(enabled);
            const blocked = permission === "denied";
            this.notificationsBlocked.next(blocked);
        }
    }

    /*
     * Watch the Browser's permission changes API * for any updates from other sources
     * https://stackoverflow.com/a/39235847
     */
    private async watchPermissionsChanges() {
        // More bad typedefs
        const nav: any = navigator;
        if (("permissions" in nav) && ("query" in nav.permissions)) {
            const query = {name: "notifications"};
            const permissions = await nav.permissions.query(query);
            permissions.onchange = () => this.updatePermissionStatus();
        }
    }

    private async initializeUserPreferences() {
        const response = await this.$http.get<NotificationPreferencesRecord>(PREFERENCES_URL);
        const data = response.data!;
        const preferences = UserNotificationPreferences.create(data);
        this.preferences$.next(preferences);
    }

    private async savePreferenceState(preferences: UserNotificationPreferences) {
        const record = preferences.record();
        this.$http.post(PREFERENCES_URL, record, {ignoreLoadingBar: true});
    }

    public get preferences(): Observable<UserNotificationPreferences> {
        return this.preferences$.observeOn(Scheduler.async);
    }

    public get available(): Observable<boolean> {
        return this.notificationsAvailable
                   .observeOn(Scheduler.async)
                   .distinctUntilChanged();
    }

    public get enabled(): Observable<boolean> {
        return this.notificationsEnabled
                   .observeOn(Scheduler.async)
                   .distinctUntilChanged();
    }

    public get blocked(): Observable<boolean> {
        return this.notificationsBlocked
                   .observeOn(Scheduler.async)
                   .distinctUntilChanged();
    }

    public update(preferences: UserNotificationPreferences) {
        this.preferences$.next(preferences);
    }

    public subscribe() {
        Observable.combineLatest(this.notificationsAvailable, this.notificationsBlocked)
                  .take(1)
                  .filter(([available, blocked]) => available && !blocked)
                  .flatMap(() => this.serviceWorkerService.registration())
                  .flatMap((reg) => this.obtainSubscription(reg))
                  .map((sub) => this.createRequest(sub))
                  .flatMap((req) => this.registerSubscription(req))
                  .do(() => this.updatePermissionStatus())
                  .finally(() => this.subscribed = true)
                  .subscribe();
    }

    private async registerSubscription(subscription: SubscriptionRequest) {
        await this.$http.post(SUBSCRIPTION_URL, subscription);
    }

    private async obtainSubscription(registration: ServiceWorkerRegistration): Promise<PushSubscription> {
        const pushManager = registration.pushManager;
        const currentSub = await pushManager.getSubscription();
        if (currentSub != null) {
            return currentSub;
        } else {
            const publicKey = await this.publicKey();
            const options = {
                userVisibleOnly: true,
                applicationServerKey: publicKey
            };
            return registration.pushManager.subscribe(options);
        }
    }

    private createRequest(subscription: PushSubscription): SubscriptionRequest {
        const key = subscription.getKey("p256dh");
        const auth = subscription.getKey("auth");
        const endpoint = subscription.endpoint;
        return {
            userKey: this.encodeKey(key),
            auth: this.encodeKey(auth),
            endpoint
        };
    }

    private encodeKey(raw: ArrayBuffer | null): string {
        if (raw == null) {
            return "";
        } else {
            const input = new Uint8Array(raw);
            return btoa(String.fromCharCode.apply(null, input));
        }
    }

    private async publicKey(): Promise<Uint8Array> {
        const response = await this.$http.get<string>(PUBLIC_KEY_URL);
        const data = response.data!;
        return this.urlB64ToUint8Array(data);
    }

    /* Taken from: https://github.com/GoogleChromeLabs/web-push-codelab/blob/master/app/scripts/main.js */
    private urlB64ToUint8Array(base64String: string): Uint8Array {
        const padding = "=".repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/\-/g, "+")
                                               .replace(/_/g, "/");

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}
